import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import ButtonIconText from 'components/ButtonIconText';
import LanguageContext from 'context/LanguageContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getOrganizationsNames } from 'services/admin-users';

interface Organization {
  id: string;
  name: string;
}

interface OrganizationAutocompleteFilterProps {
  handleOrganizationFilter: (organizationId: string | null) => void;
  handleDownloadBusinessData: () => void;
  isLoading: boolean;
  loadingDownloadCSV: boolean;
  isCreateBusiness: boolean;
  title: string;
  period?: 'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks';
  onPeriodChange?: (period: 'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks') => void;
}

const OrganizationAutocompleteFilter = ({ 
  handleOrganizationFilter, 
  handleDownloadBusinessData, 
  isLoading, 
  loadingDownloadCSV, 
  isCreateBusiness, 
  title, 
  period = 'month', 
  onPeriodChange 
}: OrganizationAutocompleteFilterProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  // Get organizations for autocomplete
  const { data: organizations, isLoading: isLoadingOrganizations } = useQuery(
    'organizations', 
    getOrganizationsNames, 
    {
      onSuccess: (data) => {
        // Reset selection if the selected organization is no longer in the list
        if (selectedOrganization && data && !data.find(org => org.id === selectedOrganization.id)) {
          setSelectedOrganization(null);
          handleOrganizationFilter(null);
        }
      },
      onError: (error) => {
        console.error('Error fetching organizations:', error);
      }
    }
  );

  const handleOrganizationChange = (event: any, newValue: Organization | null) => {
    setSelectedOrganization(newValue);
    handleOrganizationFilter(newValue ? newValue.id : null);
  };

  const handlePeriodChange = (e: any) => {
    if (onPeriodChange) {
      onPeriodChange(e.target.value);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: "144px",
        mb: "22px",
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box display={'flex'} alignItems={"center"} gap={"2rem"} flexWrap={"nowrap"} width={{ xs: "100%", md: "80%" }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '24px',
                lineHeight: '31px',
                color: '#2F3B49',
              }}
            >
              {title}
            </Typography>
          </Box>
          
          {isCreateBusiness && (
            <Box>
              <ButtonIconText
                color='primary'
                disableElevation={true}
                sx={{
                  width: '190px',
                }}
                type='submit'
                variant='contained'
                size='large'
                onClick={(e: any) => {
                  e.preventDefault();
                  navigate('/admin/add-business');
                }}
              >
                {t('ADMIN_DASHBOARD_ADD_ORGANISATION')}
              </ButtonIconText>
            </Box>
          )}
          
          <Box width={"100%"} maxWidth={"590px"}>
            <Autocomplete
              options={Array.isArray(organizations) ? organizations : []}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedOrganization}
              onChange={handleOrganizationChange}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={isLoadingOrganizations}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('ADMIN_STATS_SEARCH_BY_ORGANIZATION')}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '50px',
                      WebkitFontSmoothing: 'auto',
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingOrganizations ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                        {isLoading ? (
                          <Box display="flex" mx={1}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : null}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2">
                    {option.name}
                  </Typography>
                </Box>
              )}
              noOptionsText={t('ADMIN_STATS_NO_ORGANIZATIONS_FOUND')}
              clearText={t('ADMIN_TRANSACTION_CLEAR_FILTER')}
            />
          </Box>
        </Box>

        <Box display={'flex'} alignItems={"center"} gap={2} mx={2}>
          {/* Period Selector */}
          {onPeriodChange && (
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={period}
                onChange={handlePeriodChange}
                size="small"
                sx={{
                  height: '40px',
                  backgroundColor: '#FFFFFF',
                  '& .MuiSelect-select': {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value="month">{t('PERIOD_MONTH')}</MenuItem>
                <MenuItem value="quarter">{t('PERIOD_QUARTER')}</MenuItem>
                <MenuItem value="semester">{t('PERIOD_SEMESTER')}</MenuItem>
                <MenuItem value="year">{t('PERIOD_YEAR')}</MenuItem>
                <MenuItem value="week">{t('PERIOD_WEEK')}</MenuItem>
                <MenuItem value="two_weeks">{t('PERIOD_TWO_WEEKS')}</MenuItem>
              </Select>
            </FormControl>
          )}
         {/*  
          <ButtonIcon
            onClick={handleDownloadBusinessData}
            disabled={loadingDownloadCSV}
          >
            {loadingDownloadCSV ? (
              <CircularProgress size={24} />
            ) : (
              <FileDownloadOutlinedIcon fontSize='large' />
            )}
          </ButtonIcon> */}
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationAutocompleteFilter;